import router from 'next/router'
import { memo, useCallback, useEffect, useMemo, useState, VFC } from 'react'
import { markAsRead, markAsUnread, useDeletedGroups } from 'src/api/room'
import { MessageSideMenu } from 'src/components/MessageSideMenu'
import { useErrorMessageModal } from 'src/hooks/useModal'
import {
  CreationSourceType,
  MatchingStatusType,
  Room,
  RoomStatusType,
} from 'src/models/Room'
import { LoginUser } from 'src/models/User'
import { SWRInfiniteResponse } from 'swr/infinite'
import { useMessageContext } from './MessageContext'
import { RoomsList } from './RoomsList'

type MessagesLayoutProps = {
  user: LoginUser
  roomsArr?: Room[][]
  mutateRooms: SWRInfiniteResponse<Room[], any>['mutate']
  isLoadingInitialData: boolean
  FecthMoreTrigger: VFC
  roomName: string
  matchingStatus: MatchingStatusType
  creationSource: CreationSourceType
  roomStatus: RoomStatusType
}
export const SideMenuAndRoomList: VFC<MessagesLayoutProps> = memo(
  ({
    user,
    roomsArr,
    mutateRooms,
    isLoadingInitialData,
    FecthMoreTrigger,
    roomName,
    matchingStatus,
    creationSource,
    roomStatus,
  }) => {
    const userId = user.id
    const userPlan = user.plan
    const isReferrer = user.plan.isReferrer
    const { room: currentRoom } = useMessageContext()
    const { data: deletedGroups } = useDeletedGroups(userId, isReferrer)
    const { open } = useErrorMessageModal()

    const rooms = useMemo(
      () => (roomsArr ? ([] as Room[]).concat(...roomsArr) : []),
      [roomsArr],
    )
    const [showDeleted, setShowDeleted] = useState<boolean>(false)
    const currentRooms = useMemo(() => {
      if (showDeleted)
        return deletedGroups
          ? deletedGroups.filter((room) => keywordFilter(room, roomName))
          : []

      return rooms
    }, [rooms, deletedGroups, showDeleted, roomName])

    const markAsUnreadFn = useCallback(
      async (room: Room) => {
        const isSelectedRoom = currentRoom?.uniqueId === room.uniqueId
        const res = await markAsUnread(
          room.id,
          room.type,
          isSelectedRoom ? undefined : mutateRooms,
        )
        if (res.error) {
          open({ message: res.error.errorMessages.join('\n') })
          return
        }
        if (isSelectedRoom) router.reload()
      },
      [currentRoom?.uniqueId, mutateRooms, open],
    )

    useEffect(() => {
      if (currentRoom && !currentRoom.hasRead)
        markAsRead(currentRoom.uniqueId, userId, currentRoom.url, mutateRooms)
    }, [currentRoom, mutateRooms, userId])

    return (
      <>
        <MessageSideMenu
          hasPaid={userPlan.paid || userPlan.isAdminRole}
          isReferrer={isReferrer}
          activeMatchingStatus={matchingStatus}
          activeCreationSource={creationSource}
          activeRoomStatus={roomStatus}
          showDeleted={showDeleted}
          showTips={userPlan.paid || userPlan.isAdminRole}
          setShowDeleted={setShowDeleted}
        />
        <RoomsList
          userId={userId}
          isReferrer={isReferrer}
          isSalesDemo={userPlan.salesDemo}
          isLoadingInitialData={isLoadingInitialData}
          currentRooms={currentRooms}
          currentRoom={currentRoom}
          markAsUnreadFn={markAsUnreadFn}
          mutateRooms={mutateRooms}
          FecthMoreTrigger={FecthMoreTrigger}
          showDeleted={showDeleted}
          roomName={roomName}
          hasPaid={userPlan.paid || userPlan.isAdminRole}
          activeRoomStatus={roomStatus}
          activeMatchingStatus={matchingStatus}
        />
      </>
    )
  },
)

// OS-963 FIXME: 紹介担当が削除したグループを絞り込むために使用。API側で検索したい
const keywordFilter = (room: Room, keyword: string): boolean =>
  room.counters.some(
    (counter) =>
      counter.companyName.includes(keyword) ||
      counter.name.includes(keyword) ||
      counter.nameFurigana.includes(keyword),
  )
